import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PricingProductInput } from '../../../../../store/features/products/types';
import { PricingFormInputType } from '../../../../../types/enums';
import { useSelectPackageContext } from '../../PackageSelectionForm/selectPackageContext';
import SelectField from '../../../../../shared/formElements/SelectField';
import TextField from '../../../../../shared/formElements/TextField';
import CheckboxField from '../../../../../shared/formElements/CheckboxField';

type DataType = {
  labelKey: string;
  placeholderKey?: string;
};

const fieldsDataMap: Record<string, DataType> = {
  country: {
    labelKey:
      'site.pricing.products-selection.form.virtual-vat.field.country.label',
    placeholderKey:
      'site.pricing.products-selection.form.virtual-vat.field.country.placeholder',
  },
  region: {
    labelKey:
      'site.pricing.products-selection.form.virtual-vat.field.region.label',
    placeholderKey:
      'site.pricing.products-selection.form.virtual-vat.field.region.placeholder',
  },
  resident: {
    labelKey:
      'site.pricing.products-selection.form.virtual-vat.field.resident.label',
  },
  taxIdExemption: {
    labelKey:
      'site.pricing.products-selection.form.virtual-vat.field.tax-id-exemption.label',
  },
  vatNumber: {
    labelKey:
      'site.pricing.products-selection.form.virtual-vat.field.vat-number.label',
  },
};

export interface VirtualVatFieldProps {
  field: PricingProductInput;
}

export default function VirtualVatField(props: VirtualVatFieldProps) {
  const { field } = props;
  const { t } = useTranslation();
  const fieldData = fieldsDataMap[field.name] || {};

  const translatedCountryOptions = useMemo(() => {
    const fieldOptions = field.options || [];
    return fieldOptions.map((countryOption) => ({
      value: countryOption.value,
      label: t(countryOption.label),
    }));
  }, [field.options, t]);

  const { handleChangeSelections } = useSelectPackageContext();

  switch (field.type) {
    case PricingFormInputType.SELECT: {
      return (
        <SelectField
          name={field.name}
          options={translatedCountryOptions}
          label={t(fieldData.labelKey)}
          placeholder={fieldData.placeholderKey && t(fieldData.placeholderKey)}
          rules={{ required: field.required }}
          onChange={() => handleChangeSelections()}
        />
      );
    }

    case PricingFormInputType.TEXT: {
      return (
        <TextField
          name={field.name}
          label={t(fieldData.labelKey)}
          rules={{ required: field.required }}
        />
      );
    }

    case PricingFormInputType.CHECKBOX: {
      return (
        <CheckboxField
          name={field.name}
          value={field.value === 'true'}
          label={t(fieldData.labelKey)}
          onChange={() => handleChangeSelections()}
          sx={{ m: 0 }}
        />
      );
    }

    default: {
      return null;
    }
  }
}
